import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../context/AuthContext';
import { errorMessage } from '../lib/errors';
import { supabase } from '../lib/supabase';
import { formatDate } from '../lib/text';
import type { AdminUser, StaffRole } from '../lib/types';

export function AdminsPage() {
  const { staff } = useAuth();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<StaffRole>('editor');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canManage = staff?.role === 'owner' || staff?.role === 'admin';

  const reload = async () => {
    const { data, error: queryError } = await supabase
      .from('admin_users')
      .select('*')
      .order('created_at');
    if (queryError) throw queryError;
    setAdmins((data ?? []) as AdminUser[]);
  };

  useEffect(() => {
    void reload().catch((caught) => setError(errorMessage(caught)));
  }, []);

  const add = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    try {
      const { error: queryError } = await supabase.from('admin_users').upsert({
        user_id: userId.trim(),
        role,
        enabled: true,
      });
      if (queryError) throw queryError;
      setUserId('');
      setSuccess('Administrateur ajouté.');
      await reload();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const update = async (admin: AdminUser, changes: Partial<AdminUser>) => {
    setError('');
    try {
      const { error: queryError } = await supabase
        .from('admin_users')
        .update(changes)
        .eq('user_id', admin.user_id);
      if (queryError) throw queryError;
      await reload();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">Accès</p>
          <h1>Administrateurs</h1>
          <p>Les utilisateurs Auth doivent être créés dans Supabase avant d’être ajoutés ici.</p>
        </div>
      </header>

      {error && <div className="alert error">{error}</div>}
      {success && <div className="alert success">{success}</div>}

      <section className="two-column-grid admin-layout">
        <form className="panel form-stack" onSubmit={add}>
          <div>
            <p className="eyebrow">Autorisation</p>
            <h2>Ajouter un compte existant</h2>
          </div>
          <label>
            UUID Auth de l’utilisateur
            <input
              required
              disabled={!canManage}
              value={userId}
              placeholder="00000000-0000-0000-0000-000000000000"
              onChange={(event) => setUserId(event.target.value)}
            />
          </label>
          <label>
            Rôle
            <select disabled={!canManage} value={role} onChange={(event) => setRole(event.target.value as StaffRole)}>
              <option value="reviewer">reviewer · lecture et revue</option>
              <option value="editor">editor · modification du contenu</option>
              <option value="admin">admin · contenu et administrateurs</option>
              <option value="owner">owner · contrôle total</option>
            </select>
          </label>
          <button className="button primary" disabled={!canManage}>Ajouter</button>
          {!canManage && <p className="muted">Seuls les rôles owner et admin peuvent modifier cette liste.</p>}
        </form>

        <aside className="panel">
          <p className="eyebrow">Procédure</p>
          <h2>Créer un nouvel accès</h2>
          <ol>
            <li>Supabase → Authentication → Users.</li>
            <li>Créer l’utilisateur avec son e-mail et son mot de passe.</li>
            <li>Copier son UUID.</li>
            <li>Coller l’UUID dans ce formulaire et attribuer un rôle.</li>
          </ol>
        </aside>
      </section>

      <section className="panel table-panel">
        <div className="table-scroll">
          <table>
            <thead><tr><th>UUID</th><th>Rôle</th><th>État</th><th>Créé</th><th>Actions</th></tr></thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.user_id}>
                  <td><code>{admin.user_id}</code></td>
                  <td>
                    <select
                      disabled={!canManage}
                      value={admin.role}
                      onChange={(event) => void update(admin, { role: event.target.value as StaffRole })}
                    >
                      <option value="reviewer">reviewer</option>
                      <option value="editor">editor</option>
                      <option value="admin">admin</option>
                      <option value="owner">owner</option>
                    </select>
                  </td>
                  <td>{admin.enabled ? 'Actif' : 'Désactivé'}</td>
                  <td>{formatDate(admin.created_at)}</td>
                  <td>
                    <button
                      className="button ghost small"
                      disabled={!canManage}
                      onClick={() => void update(admin, { enabled: !admin.enabled })}
                    >
                      {admin.enabled ? 'Désactiver' : 'Activer'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
